import { makeAutoObservable, runInAction } from 'mobx'
import { type AxiosResponse, type CancelToken } from 'axios'
import { DayjsFormats, numberFormat } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { RequestLoadData } from 'shared/api/lib'
import { type IResponseGetBillingPreview } from 'entities/Billing/api/types'
import { smartRound } from 'entities/Billing/lib/smartRound'
import { type PriceInfoViewModel } from 'entities/Billing/model/PriceInfoViewModel'

export class PriceInfoStore implements PriceInfoViewModel {
  private _priceInfo: IResponseGetBillingPreview | null = null

  constructor(
    private _request: (
      count: number,
      cancelToken?: CancelToken
    ) => Promise<AxiosResponse<IResponseGetBillingPreview>>
  ) {
    makeAutoObservable(this)
  }

  get requestInfoStatus() {
    return this._requestInfoStatus
  }

  get priceInfo() {
    return this._priceInfo
  }

  get priceInfoUiData() {
    if (!this.priceInfo) return null

    const tax_rate = this.priceInfo.tax_rates.length ? this.priceInfo.tax_rates[0] : null
    const noConvertedNextChargeDate =
      this.priceInfo.next_charge_date ?? this.priceInfo.next_charge_details.date

    return {
      tierDetails: this.priceInfo.tier_details?.map((detail) => ({
        tier: detail.tier,
        count: detail.count,
        perPrice: numberFormat({
          value: detail.perPrice / 100,
          currency: 'USD',
          minimumFractionDigits: 2,
        }),
      })),
      interval: this.priceInfo.interval,
      subtotal: numberFormat({
        value: this.priceInfo.subtotal / 100,
        currency: 'USD',
        minimumFractionDigits: 2,
      }),
      unusedTime: numberFormat({
        value: (this.priceInfo.unused_time * -1) / 100,
        currency: 'USD',
        minimumFractionDigits: 2,
      }),
      taxes: tax_rate
        ? {
            total: numberFormat({
              value: this.priceInfo.taxes_amount / 100,
              currency: 'USD',
              minimumFractionDigits: 2,
            }),
            percent: smartRound(tax_rate.percent, 1),
            jurisdiction: tax_rate.jurisdiction,
          }
        : null,
      totalDueToday: numberFormat({
        value: this.priceInfo.total_due_today / 100,
        currency: 'USD',
        minimumFractionDigits: 2,
      }),
      nextChargeDate: uiStore.dayjs(noConvertedNextChargeDate).format(DayjsFormats.full6),
      noConvertedNextChargeDate,
      nextCharge: numberFormat({
        value: (this.priceInfo.next_charge ?? this.priceInfo.next_charge_details.total) / 100,
        currency: 'USD',
        minimumFractionDigits: 2,
      }),
    }
  }

  private _requestInfoStatus = new RequestLoadData<number>({
    requestCallback: async ({ cancelRequest }, count) => {
      if (!count) return

      const response = await this._request(count, cancelRequest.token)

      runInAction(() => {
        this._priceInfo = response.data
      })
    },
  })

  loadPriceData = async (count = 1) => {
    return this._requestInfoStatus.loadData(count)
  }

  resetRequest = () => {
    this._requestInfoStatus.dispose()
  }

  reset = () => {
    this._priceInfo = null
    this._requestInfoStatus.dispose()
  }
}
